'use strict';

import axios from 'axios';
import { listenDOMEvent } from '@jspartials/dom-api-utils';
import {
  classes,
  addClassToElement,
  removeClassToElement,
  hasClassElement
} from '@jspartials/class-list-utils';
import { addToCartItem } from '@jspartials/cart-utils';
import { gtmGetListName, gtmListenProductTiles } from '@jspartials/gtm-utils';
import { decodeBase64 } from '@jspartials/string-utils';
import { resetImagesCarousel } from '@jspages/search/imagesCarouselTiles';
import {
  mediaQuery,
  getMediaQuery,
  tryExecute
} from '@jspartials/general-utils';

// Variables
const timeoutToHideStatusClassnamesOnAddToCartButton = 3000;
const placeholderImage =
  "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' style='background:whitesmoke' viewBox='0 0 96 122'></svg>";
let listOfProductTiles = [];

// Selectors
const selector = {
  addToCart: {
    wrapper: '.js-add-to-cart',
    button: '.js-add-to-cart__button',
    buttonOpenList: '.js-add-to-cart__button-open-list',
    list: '.js-add-to-cart__variants-list'
  },
  imagesCarousel: {
    wrapper: '.js-images-carousel',
    content: '.js-images-carousel__content',
    item: '.js-images-carousel__item'
  },
  product: {
    tile: '.js-product-tile',
    imagePrimary: '.js-product-tile__image--primary',
    imageSecondary: '.js-product-tile__image--secondary',
    title: '.js-product-tile__title',
    price: '.js-product-tile__price',
    link: '.js-product-tile__link',
    addToCart: '.js-product-tile__add-to-cart',
    virtualTryOnButton: '.js-product-tile__vto-button',
    attributesWrapper: '.js-product-tile__attributes-wrapper',
    promoLabelWrapper: '.js-product-tile__promo-label-wrapper',
    marketingLabelWrapper: '.js-product-tile__marketing-label-wrapper',
    messageSoldOut: '.js-product-tile__message-sold-out',
    messageTotalVariants: '.js-product-tile__message-total-variants',
    colorVariants: '.js-product-color-variants',
    colorVariantsList: '.js-product-color-variants__list',
    colorVariantsListItem: '.js-product-color-variants__list-item',
    colorVariantsListItemImage: '.js-product-color-variants__list-item-image',
    colorVariantsNavigationButton:
      '.js-product-color-variants__navigation-button',
    colorVariantsNavigationButtonLeft:
      '.js-product-color-variants__navigation-button--left',
    colorVariantsNavigationButtonRight:
      '.js-product-color-variants__navigation-button--right'
  }
};
const mediaQueryTablet = getMediaQuery(mediaQuery.tablet);

// Functions

/**
 * Add the element to the list of observed product tiles
 * @param {HTMLElement} element - The element to add into the list
 * @param {Array} list - The list to add the element
 * @returns {void}
 */
const addToList = (element, list) => {
  list.push(element);
};

/**
 * Listen the product tiles in the viewport
 * @param {boolean} gtmResetList - If the list of product tiles in GTM should be reset
 * @returns {void}
 */
const listenProductTiles = (gtmResetList = false) => {
  const productTiles = document.querySelectorAll(selector.product.tile);

  if (productTiles.length > 0) {
    productTiles.forEach((productTile) => {
      if (!listOfProductTiles.includes(productTile)) {
        addToList(productTile, listOfProductTiles);
        listenProductAddToCart(productTile);
        listenProductColorVariants(productTile);
        observeProductColorVariants(productTile);
      }
    });

    gtmListenProductTiles(gtmResetList);
  }
};

/**
 * Observe the color variants of the product tile and show them when they are in the viewport
 * @param {HTMLElement} productTile - The product tile element
 * @returns {void}
 */
const observeProductColorVariants = (productTile) => {
  const colorVariantsListItems = productTile.querySelectorAll(
    selector.product.colorVariantsListItem
  );

  colorVariantsListItems.forEach((colorVariantsListItem) => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const colorVariant = entry.target;
          const productId = colorVariant?.dataset?.pid || '';
          const isCached = hasClassElement(colorVariant, classes.is.cached);

          if (productId && !isCached) {
            tryExecute(showProductColorVariant, 5, 1000, productId)
              .then((response) => {
                if (!response) {
                  throw new Error(
                    `Color variant info not available for ${productId}`
                  );
                }

                updateInfoProductColorVariant(colorVariant, response);
                listenColorVariantListItemClick(colorVariant);
              })
              .catch((error) => {
                console.error(error);
              });
          } else {
            listenColorVariantListItemClick(colorVariant);
          }
          observer.disconnect();
        }
      });
    });

    observer.observe(colorVariantsListItem);
  });
};

/**
 * Show the product color variant
 * @param {HTMLElement} colorVariant - The color variant element
 * @returns {void}
 */
const showProductColorVariant = async (productId) => {
  try {
    const response = await axios.post(
      globalVariables.endpoint.getInfoVariantProductTile,
      {
        id: productId
      },
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response;
  } catch (error) {
    if (error.response) {
      return error.response;
    }
    throw error;
  }
};

/**
 * Update the info for the product color variant and data attributes
 * @param {HTMLElement} colorVariant - The color variant element
 * @param {Object} data - The data to update
 * @returns {void}
 */
const updateInfoProductColorVariant = (colorVariant, data) => {
  const imageElement =
    colorVariant.querySelector(selector.product.colorVariantsListItemImage) ||
    null;

  // Remove the loading class
  removeClassToElement(colorVariant, classes.is.loading);

  // Update the thumbnail image
  if (imageElement) {
    imageElement.src = data.images.thumbnail.url;
    imageElement.alt = data.images.thumbnail.alt;
  }

  // Update the data attribute for name
  colorVariant.dataset.name = data.name;

  // Update the data attribute for url
  colorVariant.dataset.url = data.url;

  // Update the data attribute for images
  colorVariant.dataset.imagePrimary = data.images.primary.url;
  colorVariant.dataset.imageSecondary = data.images.secondary.url;
  colorVariant.dataset.carouselImages = JSON.stringify(
    data.carouselImages || []
  );

  // Update the data attribute for price
  colorVariant.dataset.price = data.priceHtmlEncoded;

  // Update the data attribute for add to cart button
  colorVariant.dataset.addToCart = data.addToCartButtonHtmlEncoded;

  // Update the data attributes for VTO
  colorVariant.dataset.vtoEnabled = data.vto.enabled;
  colorVariant.dataset.vtoUrl = data.vto.url;

  // Update the data attributes for labels
  colorVariant.dataset.attributes = data.labels.attributesHtmlEncoded;
  colorVariant.dataset.labelPromo = data.labels.promoHtmlEncoded;
  colorVariant.dataset.labelMarketing = data.labels.marketingHtmlEncoded;

  // Update the data attribute for messages
  colorVariant.dataset.messageAvailable = data.available;
};

/**
 * Listen the color variants of the product tile
 * @param {HTMLElement} productTile - The product tile element
 * @returns {void}
 */
const listenProductColorVariants = (productTile) => {
  const colorVariants = productTile.querySelector(
    selector.product.colorVariants
  );

  if (colorVariants) {
    listenColorVariantsNavigation(colorVariants);
  }
};

/**
 * Listen the click and hover events on the color variant list item
 * @param {HTMLElement} colorVariants - The color variants element
 * @returns {void}
 */
const listenColorVariantListItemClick = (colorVariantsListItem) => {
  // On click
  listenDOMEvent(colorVariantsListItem, 'click', () => {
    updateProductTileInfo(colorVariantsListItem, true);
    selectColorVariantsListItem(colorVariantsListItem);
  });

  // On hover in
  listenDOMEvent(colorVariantsListItem, 'mouseover', () => {
    // Media query is needed in safari on the iphone cause the variant selection doesn't work properly
    if (mediaQueryTablet.matches) {
      updateProductTileInfo(colorVariantsListItem);
    }
  });

  // On hover out
  listenDOMEvent(colorVariantsListItem, 'mouseout', () => {
    // Media query is needed in safari on the iphone cause the variant selection doesn't work properly
    if (mediaQueryTablet.matches) {
      const colorVariantsList = colorVariantsListItem.closest(
        selector.product.colorVariantsList
      );
      const colorVariantsListItemSelected = colorVariantsList
        ? colorVariantsList.querySelector(
            `${selector.product.colorVariantsListItem}.${classes.is.selected}`
          )
        : null;

      if (colorVariantsListItemSelected) {
        updateProductTileInfo(colorVariantsListItemSelected);
      }
    }
  });
};

/**
 * Select the color variant list item
 * @param {HTMLElement} listItem - The color variant list item element
 * @returns {void}
 */
const selectColorVariantsListItem = (listItem) => {
  const list = listItem.closest(selector.product.colorVariantsList);
  const currentListItemSelected = list.querySelector(
    `${selector.product.colorVariantsListItem}.${classes.is.selected}`
  );

  if (list && currentListItemSelected) {
    removeClassToElement(currentListItemSelected, classes.is.selected);
    addClassToElement(listItem, classes.is.selected);
  }
};

/**
 * Listen the navigation on the color variants
 * @param {HTMLElement} colorVariants - The color variants element
 * @returns {void}
 */
const listenColorVariantsNavigation = (colorVariants) => {
  const colorVariantsNavigationButtons = colorVariants.querySelectorAll(
    selector.product.colorVariantsNavigationButton
  );

  if (colorVariantsNavigationButtons.length > 0) {
    colorVariantsNavigationButtons.forEach((colorVariantsNavigationButton) => {
      listenDOMEvent(colorVariantsNavigationButton, 'click', () => {
        navigateColorVariants(colorVariantsNavigationButton);
      });
    });
  }
};

/**
 * Navigate the color variants
 * @param {HTMLElement} navigationButton - The color variants navigation button element
 * @returns {void}
 */
const navigateColorVariants = (navigationButton) => {
  const direction = navigationButton.dataset.direction;
  const wrapper = navigationButton.closest(selector.product.colorVariants);
  const list = wrapper
    ? wrapper.querySelector(selector.product.colorVariantsList)
    : null;
  const distance = list
    ? list.querySelector(selector.product.colorVariantsListItem).clientWidth * 4
    : 0;
  const distanceToMove = direction === 'left' ? -distance : distance;

  if (wrapper && list) {
    moveColorVariantsList(wrapper, list, distanceToMove);
  }
};

/**
 * Move the color variants list
 * @param {HTMLElement} wrapper - The color variants wrapper element
 * @param {HTMLElement} list - The color variants list element
 * @param {number} distance - The distance to move
 * @returns {void}
 */
const moveColorVariantsList = (wrapper, list, distance) => {
  const navigationButtonLeft = wrapper.querySelector(
    selector.product.colorVariantsNavigationButtonLeft
  );
  const navigationButtonRight = wrapper.querySelector(
    selector.product.colorVariantsNavigationButtonRight
  );

  list.scrollBy({
    left: distance,
    behavior: 'smooth'
  });

  if (navigationButtonLeft && navigationButtonRight) {
    setTimeout(() => {
      if (list.scrollLeft > 0) {
        addClassToElement(navigationButtonLeft, classes.is.visible);
      }

      // Scroll left ends
      if (list.scrollLeft === 0) {
        removeClassToElement(navigationButtonLeft, classes.is.visible);
        addClassToElement(navigationButtonRight, classes.is.visible);
      }

      // Scroll right ends
      if (list.scrollLeft >= list.scrollWidth - list.clientWidth) {
        removeClassToElement(navigationButtonRight, classes.is.visible);
      } else {
        addClassToElement(navigationButtonRight, classes.is.visible);
      }
    }, 500);
  }
};

/**
 * Listen the add to cart of the product tile
 * @param {HTMLElement} parentElement - The parent element
 * @returns {void}
 */
const listenProductAddToCart = (parentElement) => {
  const addToCartWrapper = parentElement.querySelector(
    selector.addToCart.wrapper
  );
  const addToCartButtons = addToCartWrapper
    ? addToCartWrapper.querySelectorAll(selector.addToCart.button)
    : [];
  const addToCartButtonOpenList = addToCartWrapper
    ? addToCartWrapper.querySelector(selector.addToCart.buttonOpenList)
    : null;
  const addToCartList = addToCartWrapper
    ? addToCartWrapper.querySelector(selector.addToCart.list)
    : null;
  const gtmListName = gtmGetListName(parentElement);

  if (addToCartButtons.length > 0) {
    addToCartButtons.forEach((addToCartButton) => {
      const productId = addToCartButton.dataset.pid || null;

      if (productId) {
        listenDOMEvent(addToCartButton, 'click', () => {
          if (addToCartList) {
            removeClassToElement(addToCartList, classes.is.visible);
          }

          setLoadingAddToCart(addToCartWrapper);

          addToCartItem(productId, 1, gtmListName, [])
            .then(() => {
              setSuccessAddToCart(addToCartWrapper);

              document.dispatchEvent(
                new CustomEvent(
                  globalVariables.customEvent
                    .productTileAddedToCartSuccessfully,
                  {
                    detail: {
                      productId: productId,
                      gtmListName: gtmListName
                    }
                  }
                )
              );
            })
            .catch((error) => {
              setErrorAddToCart(addToCartWrapper);

              if (error.response.status === 410) {
                document.dispatchEvent(
                  new CustomEvent(
                    globalVariables.customEvent
                      .productTileAddedToCartSuccessfully,
                    {
                      detail: {
                        productId: productId,
                        errorMessage: error.response.data.message
                      }
                    }
                  )
                );
              }
              console.error(error);
            });
        });
      }
    });
  }

  if (addToCartButtonOpenList && addToCartList) {
    listenDOMEvent(addToCartButtonOpenList, 'mouseover', () => {
      addClassToElement(addToCartList, classes.is.visible);
    });

    listenDOMEvent(addToCartList, 'mouseleave', () => {
      removeClassToElement(addToCartList, classes.is.visible);
    });
  }
};

/**
 * Set the success status on the add to cart button
 * @param {HTMLElement} element - The add to cart button element
 * @returns {void}
 */
const setSuccessAddToCart = (element) => {
  unsetLoadingAddToCart(element);
  addClassToElement(element, classes.has.success);

  setTimeout(() => {
    removeClassToElement(element, classes.has.success);
  }, timeoutToHideStatusClassnamesOnAddToCartButton);
};

/**
 * Set the error status on the add to cart button
 * @param {HTMLElement} element - The add to cart button element
 * @returns {void}
 */
const setErrorAddToCart = (element) => {
  unsetLoadingAddToCart(element);
  addClassToElement(element, classes.has.error);

  setTimeout(() => {
    removeClassToElement(element, classes.has.error);
  }, timeoutToHideStatusClassnamesOnAddToCartButton);
};

/**
 * Set the loading status on the add to cart button
 * @param {HTMLElement} element - The add to cart button element
 * @returns {void}
 */
const setLoadingAddToCart = (element) => {
  addClassToElement(element, classes.is.loading);
};

/**
 * Unset the loading status on the add to cart button
 * @param {HTMLElement} element - The add to cart button element
 * @returns {void}
 */
const unsetLoadingAddToCart = (element) => {
  removeClassToElement(element, classes.is.loading);
};

/**
 * Update the info for the product tile
 * @param {HTMLElement} colorVariantItem - The color variant item element
 * @param {boolean} clicked - If the color variant item was clicked
 * @returns {void}
 */
const updateProductTileInfo = (colorVariantItem, clicked = false) => {
  const productTile = colorVariantItem.closest(selector.product.tile) || null;

  if (productTile) {
    updateProductTileImages(productTile, colorVariantItem, clicked);
    updateProductTileTitle(productTile, colorVariantItem);
    updateProductTilePrice(productTile, colorVariantItem);
    updateProductTilePromoLabel(productTile, colorVariantItem);
    updateProductTileMarketingLabel(productTile, colorVariantItem);
    updateProductTileVirtualTryOn(productTile, colorVariantItem);
    updateProductTileAttributes(productTile, colorVariantItem);
    updateProductTileMessages(productTile, colorVariantItem);

    if (clicked) {
      // Se conserva el pid con el que el servidor pintó el tile antes de
      // sobrescribirlo: es el que volverá a pintar al recargar la PLP, y el que
      // necesita la restauración de scroll al volver de la PDP. Solo la primera
      // vez, para no perderlo si se cambia de color varias veces.
      // Ver partials/plp-target-product.js.
      if (!productTile.dataset.defaultPid) {
        productTile.dataset.defaultPid = productTile.dataset.pid;
      }

      productTile.dataset.pid = colorVariantItem.dataset.pid;
      updateProductTileLink(productTile, colorVariantItem);
      updateProductAddToCart(productTile, colorVariantItem);
    }
  }
};

/**
 * Update the images for the product tile
 * @param {HTMLElement} productTile - The product tile element
 * @param {HTMLElement} colorVariantItem - The color variant item element
 * @param {boolean} clicked - If the color variant item was clicked
 * @returns {void}
 */
const updateProductTileImages = (
  productTile,
  colorVariantItem,
  clicked = false
) => {
  const productTileImagePrimary = productTile.querySelector(
    selector.product.imagePrimary
  );
  const productTileImageSecondary = productTile.querySelector(
    selector.product.imageSecondary
  );
  const imagesCarousel =
    productTile.querySelector(selector.imagesCarousel.wrapper) || null;
  const productName = colorVariantItem.dataset.name || '';
  const imagePrimary = colorVariantItem.dataset.imagePrimary || null;
  const imageSecondary = colorVariantItem.dataset.imageSecondary || null;
  // With images carousel, the hover only previews the primary image. The
  // secondary image and the carousel images are updated on click
  const updateSecondaryImage = !imagesCarousel || clicked;

  if (productTileImagePrimary && imagePrimary) {
    productTileImagePrimary.src = imagePrimary;
    productTileImagePrimary.alt = productName;
  }

  if (productTileImageSecondary && imageSecondary && updateSecondaryImage) {
    productTileImageSecondary.src = imageSecondary;
    productTileImageSecondary.alt = productName;
  }

  if (imagesCarousel) {
    // Go back to the first slide to show the primary image of the variant,
    // without the animation of the carousel
    resetImagesCarousel(imagesCarousel);

    if (clicked && productTile.dataset.pid !== colorVariantItem.dataset.pid) {
      updateProductTileCarouselImages(imagesCarousel, colorVariantItem);
    }
  }
};

/**
 * Replace the carousel images of the product tile with the ones of the color variant.
 * The first slide (primary and secondary images) is kept and the rest are rebuilt,
 * because each color variant can have a different number of images
 * @param {HTMLElement} imagesCarousel - The images carousel element
 * @param {HTMLElement} colorVariantItem - The color variant item element
 * @returns {void}
 */
const updateProductTileCarouselImages = (imagesCarousel, colorVariantItem) => {
  const imagesCarouselContent =
    imagesCarousel.querySelector(selector.imagesCarousel.content) || null;
  const imagesCarouselItems = imagesCarousel.querySelectorAll(
    selector.imagesCarousel.item
  );
  const productTileImagePrimary =
    imagesCarousel.querySelector(selector.product.imagePrimary) || null;
  const productName = colorVariantItem.dataset.name || '';
  let carouselImages = [];

  try {
    carouselImages = JSON.parse(
      colorVariantItem.dataset.carouselImages || '[]'
    );
  } catch (error) {
    console.error(error);
  }

  if (!imagesCarouselContent) {
    return;
  }

  // Remove all slides except the first one
  imagesCarouselItems.forEach((imagesCarouselItem, index) => {
    if (index > 0) {
      imagesCarouselItem.remove();
    }
  });

  // Add the slides of the color variant (the carousel loads their images
  // when the user interacts with it)
  const fragment = document.createDocumentFragment();

  carouselImages.forEach((carouselImage) => {
    const item = document.createElement('div');
    const image = document.createElement('img');

    item.className = 'tw-min-w-full js-images-carousel__item';
    image.src = placeholderImage;
    image.dataset.src = carouselImage;
    image.alt = productName;
    image.className =
      'tw-block tw-w-full tw-bg-whitesmoke js-images-carousel__image';

    if (productTileImagePrimary) {
      image.width = productTileImagePrimary.getAttribute('width');
      image.height = productTileImagePrimary.getAttribute('height');
    }

    item.appendChild(image);
    fragment.appendChild(item);
  });

  imagesCarouselContent.appendChild(fragment);
};

/**
 * Update the title for the product tile
 * @param {HTMLElement} productTile - The product tile element
 * @param {HTMLElement} colorVariantItem - The color variant item element
 * @returns {void}
 */
const updateProductTileTitle = (productTile, colorVariantItem) => {
  const productTileTitle = productTile.querySelector(selector.product.title);
  const productName = colorVariantItem.dataset.name || '';

  if (productTileTitle) {
    productTileTitle.innerHTML = productName;
  }
};

/**
 * Update the price for the product tile
 * @param {HTMLElement} productTile - The product tile element
 * @param {HTMLElement} colorVariantItem - The color variant item element
 * @returns {void}
 */
const updateProductTilePrice = (productTile, colorVariantItem) => {
  const productTilePrice = productTile.querySelector(selector.product.price);
  const productPriceHtmlEncoded = colorVariantItem.dataset.price || '';

  if (productTilePrice) {
    productTilePrice.innerHTML = decodeBase64(productPriceHtmlEncoded);
  }
};

/**
 * Update the link for the product tile
 * @param {HTMLElement} productTile - The product tile element
 * @param {HTMLElement} colorVariantItem - The color variant item element
 * @returns {void}
 */
const updateProductTileLink = (productTile, colorVariantItem) => {
  const productTileLinks = productTile.querySelectorAll(selector.product.link);
  const productUrl = colorVariantItem.dataset.url || '';

  if (productTileLinks.length > 0) {
    productTileLinks.forEach((productTileLink) => {
      productTileLink.href = productUrl;
    });
  }
};

/**
 * Update the add to cart for the product tile
 * @param {HTMLElement} productTile - The product tile element
 * @param {HTMLElement} colorVariantItem - The color variant item element
 * @returns {void}
 */
const updateProductAddToCart = (productTile, colorVariantItem) => {
  const productTileAddToCart = productTile.querySelector(
    selector.product.addToCart
  );
  const productAddToCartHtmlEncoded = colorVariantItem.dataset.addToCart || '';

  if (productTileAddToCart) {
    productTileAddToCart.innerHTML = decodeBase64(productAddToCartHtmlEncoded);
    listenProductAddToCart(productTileAddToCart);
  }
};

/**
 * Update the virtual try on for the product tile
 * @param {HTMLElement} productTile - The product tile element
 * @param {HTMLElement} colorVariantItem - The color variant item element
 * @returns {void}
 */
const updateProductTileVirtualTryOn = (productTile, colorVariantItem) => {
  const productTileVirtualTryOnButton = productTile.querySelector(
    selector.product.virtualTryOnButton
  );
  const productId = colorVariantItem.dataset.pid || '';
  const vtoEnabled = colorVariantItem.dataset.vtoEnabled
    ? JSON.parse(colorVariantItem.dataset.vtoEnabled)
    : false;
  const vtoUrl = colorVariantItem.dataset.vtoUrl || '';

  if (productTileVirtualTryOnButton) {
    if (vtoEnabled) {
      productTileVirtualTryOnButton.dataset.frame = productId;
      productTileVirtualTryOnButton.dataset.url = vtoUrl;
      addClassToElement(productTileVirtualTryOnButton, classes.is.visible);
    } else {
      removeClassToElement(productTileVirtualTryOnButton, classes.is.visible);
    }
  }
};

/**
 * Update the attributes for the product tile
 * @param {HTMLElement} productTile - The product tile element
 * @param {HTMLElement} colorVariantItem - The color variant item element
 * @returns {void}
 */
const updateProductTileAttributes = (productTile, colorVariantItem) => {
  const productTileAttributesWrapper = productTile.querySelector(
    selector.product.attributesWrapper
  );
  const attributes = colorVariantItem.dataset.attributes || '';

  if (productTileAttributesWrapper) {
    productTileAttributesWrapper.innerHTML = decodeBase64(attributes);
  }
};

/**
 * Update the promo label for the product tile
 * @param {HTMLElement} productTile - The product tile element
 * @param {HTMLElement} colorVariantItem - The color variant item element
 * @returns {void}
 */
const updateProductTilePromoLabel = (productTile, colorVariantItem) => {
  const productTilePromoLabelWrapper = productTile.querySelector(
    selector.product.promoLabelWrapper
  );
  const labelPromoHtmlEncoded = colorVariantItem.dataset.labelPromo || '';

  if (productTilePromoLabelWrapper) {
    productTilePromoLabelWrapper.innerHTML = decodeBase64(
      labelPromoHtmlEncoded
    );
  }
};

/**
 * Update the marketing label for the product tile
 * @param {HTMLElement} productTile - The product tile element
 * @param {HTMLElement} colorVariantItem - The color variant item element
 * @returns {void}
 */
const updateProductTileMarketingLabel = (productTile, colorVariantItem) => {
  const productTileMarketingLabelWrapper = productTile.querySelector(
    selector.product.marketingLabelWrapper
  );
  const labelMarketingHtmlEncoded =
    colorVariantItem.dataset.labelMarketing || '';

  if (productTileMarketingLabelWrapper) {
    productTileMarketingLabelWrapper.innerHTML = decodeBase64(
      labelMarketingHtmlEncoded
    );
  }
};

/**
 * Update the messages for the product tile
 * @param {HTMLElement} productTile - The product tile element
 * @param {HTMLElement} colorVariantItem - The color variant item element
 * @returns {void}
 */
const updateProductTileMessages = (productTile, colorVariantItem) => {
  const productTileMessageSoldOut =
    productTile.querySelector(selector.product.messageSoldOut) || null;
  const productTileMessageTotalVariants =
    productTile.querySelector(selector.product.messageTotalVariants) || null;
  const productAvailable = colorVariantItem?.dataset?.messageAvailable;
  const productAvailableValue = productAvailable
    ? JSON.parse(productAvailable)
    : false;

  if (productTileMessageSoldOut) {
    if (!productAvailableValue) {
      removeClassToElement(productTileMessageSoldOut, classes.hidden);
    } else {
      addClassToElement(productTileMessageSoldOut, classes.hidden);
    }
  }

  if (productTileMessageTotalVariants) {
    if (!productAvailableValue) {
      addClassToElement(productTileMessageTotalVariants, classes.is.invalid);
    } else {
      removeClassToElement(productTileMessageTotalVariants, classes.is.invalid);
    }
  }
};

// Export all functions
export { listenProductTiles };
